import React, { Fragment, useMemo } from 'react'
import { getKeyString } from '@corratech/pylot-utils'
import { useTranslation } from 'next-i18next'
import { useCmsBlocks } from '@pylot-data/hooks/cms/use-cms-blocks'
import { getCMSBlock } from '@components/common/CMSBlockConst/cms-block-util'
import { RichContent } from '@corratech/pylot-rich-content'

type GridPageInput<T> = {
    items: T[]
    ProductTile: React.ComponentType<T>
    getMarketing: (idx: number) => JSX.Element | null
    startingIndex: number
    processedImageDisruptorData?: Array<{
        recordId: string
        blockPosition: number
        blockId: string
        startIdx: number
    }>
}

export const GridPage = ({
    items,
    ProductTile,
    getMarketing,
    processedImageDisruptorData,
    startingIndex
}: GridPageInput<any>): JSX.Element => {
    const keyString = useMemo(() => getKeyString(items), [items])

    const { t } = useTranslation('common')

    const blockIdentifiers = processedImageDisruptorData?.map(
        (disruptor) => disruptor.blockId.split('---')[0]
    )

    const { items: CmsBlocks } = useCmsBlocks(blockIdentifiers ?? [], {
        revalidateOnMount: true,
        revalidateOnFocus: false
    })

    const itemElements = useMemo(() => {
        const elements = []
        let productIndex = 0

        for (let idx = 0; idx < items.length; idx++) {
            const globalIndex = startingIndex + idx

            const disruptor = processedImageDisruptorData?.find(
                (item) => item.startIdx === globalIndex
            )

            if (disruptor) {
                const refinedBlockId = disruptor.blockId.split('---')[0]
                const disruptorContent = getCMSBlock(refinedBlockId, CmsBlocks)

                elements.push(
                    <Fragment key={`disruptor-${globalIndex}`}>
                        <div
                            style={{
                                gridColumn: `span 2`,
                                gridRow: `auto`
                            }}
                            className="image-disruptor-wrapper"
                        >
                            {disruptorContent ? (
                                <RichContent
                                    html={getCMSBlock(
                                        refinedBlockId,
                                        CmsBlocks
                                    )}
                                />
                            ) : (
                                <p>{t('No content available')}</p>
                            )}
                        </div>
                    </Fragment>
                )
            }

            elements.push(
                <Fragment key={`product-${globalIndex}`}>
                    {getMarketing(globalIndex)}
                    <ProductTile product={items[productIndex]} />
                </Fragment>
            )

            productIndex++
        }

        return elements
    }, [
        items,
        processedImageDisruptorData,
        getMarketing,
        CmsBlocks,
        startingIndex
    ])

    return <>{itemElements}</>
}
